import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import type { TicketSummary } from '@vd/types';
import { CardSession } from './CardSession';

/**
 * The Session window a card's Session button opens (TOADO-945), held once at
 * the app's shell so every list of cards (board views, inbox pages, search,
 * archive) opens the same window over itself without passing a handler
 * through each layer. Closing it gives focus back to the button pressed.
 */
type OpenSession = (ticket: Pick<TicketSummary, 'id'>, anchor: HTMLElement) => void;

const CardSessionContext = createContext<OpenSession | undefined>(undefined);

export function CardSessionProvider({ children }: { children: ReactNode }) {
  const [ticketId, setTicketId] = useState<string | null>(null);
  const anchor = useRef<HTMLElement | null>(null);
  const open = useCallback<OpenSession>((ticket, from) => {
    anchor.current = from;
    setTicketId(ticket.id);
  }, []);
  const close = useCallback(() => {
    setTicketId(null);
    // After the window has gone, so its own focus handling does not take it back.
    const from = anchor.current;
    requestAnimationFrame(() => { if (from?.isConnected) from.focus(); });
  }, []);
  const value = useMemo(() => open, [open]);
  return (
    <CardSessionContext.Provider value={value}>
      {children}
      <CardSession ticketId={ticketId} onClose={close} />
    </CardSessionContext.Provider>
  );
}

/** Opens a ticket's Session window from its card; undefined outside the shell, so the button stays hidden there. */
export function useOpenCardSession(): OpenSession | undefined {
  return useContext(CardSessionContext);
}
